import { useState, useEffect, useCallback, createContext, useContext } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setLightMode: () => void;
  setDarkMode: () => void;
  isDark: boolean;
}

export const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: 'dark',
  toggleMode: () => {},
  setLightMode: () => {},
  setDarkMode: () => {},
  isDark: true,
});

export const useThemeMode = () => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('menumaster-theme-mode');
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem('menumaster-theme-mode', mode);
    // Update document class for CSS variables
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      const saved = localStorage.getItem('menumaster-theme-mode');
      if (!saved) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const setLightMode = useCallback(() => setMode('light'), []);
  const setDarkMode = useCallback(() => setMode('dark'), []);

  return {
    mode,
    toggleMode,
    setLightMode,
    setDarkMode,
    isDark: mode === 'dark',
  };
};

export const useThemeModeContext = () => useContext(ThemeModeContext);

export default useThemeMode;
